import React, { useCallback } from 'react';
import Button from '@99designs/frontend-components/components/Button/Button';
import { formatMessage } from '@99designs/frontend-components/i18n';
import TrackScrollToPageElement from '../../../../shared/components/Analytics/TrackScrollToPageElement';
import AvatarWithDesignerMeta from '../../../../shared/components/AvatarWithDesignerMeta';
import Box from '../../../../shared/components/Box';
import { DesignerCard } from '../../../../shared/components/Card';
import Carousel from '../../../../shared/components/Carousel';
import DesignCard from '../../../../shared/components/DesignCard';
import Heading from '../../../../shared/components/Heading';
import { CTALink } from '../../../../shared/components/Link';
import Scrollable from '../../../../shared/components/Scrollable';
import {
    avatarPath,
    designerSearchPath,
    invitePath,
    profilePath,
    reviewsPath,
} from '../../../../shared/lib/platformUrls';
import { spaces } from '../../../../shared/lib/styles';

interface Props {
    designers: Designer[];
    contestCategory: {
        title: string;
        key: string;
    };
}

export interface Designer {
    id: string;
    user: {
        displayName: string;
    };
    review: {
        rating: number;
        count: number;
    };
    designerLevel: 'TOP' | 'MID' | 'ENTRY';
    designs: Design[];
}

interface Design {
    description?: string;
    id?: number;
    image?: string;
    title?: string;
}

function Designers({ designers, contestCategory }: Props) {
    const scrollableRef = useCallback((element) => {
        if (element) {
            element.center();
        }
    }, []);

    return (
        <>
            <TrackScrollToPageElement pageName="Contest Overview" pageElement="Designers Section">
                <div className="row">
                    <div className="column small-12 medium-12 normal-5 large-4">
                        <Box paddingBottom={['space10', 'space10', 'space10', 'space10']}>
                            <Heading type="h2" variations={['h1', 'no-margin']}>
                                {__('Along the way, they met lots of talented designers…')}
                            </Heading>
                        </Box>
                    </div>
                </div>
            </TrackScrollToPageElement>
            <Scrollable
                variations={['full-page-width', 'padded']}
                ref={scrollableRef}
                style={{ paddingBottom: spaces.space8 }}
            >
                {designers.map((designer) => {
                    return (
                        <div key={designer.id} style={{ width: 370 }}>
                            <DesignerCard
                                meta={
                                    <AvatarWithDesignerMeta
                                        designerId={designer.id}
                                        displayName={designer.user.displayName}
                                        level={designer.designerLevel}
                                        avgRating={designer.review.rating}
                                        totalRatings={designer.review.count}
                                        ctaUrl={reviewsPath(designer.id)}
                                        isLinked={false}
                                    />
                                }
                                gallery={
                                    <Carousel
                                        items={designer.designs.map((design) => {
                                            return (
                                                <DesignCard
                                                    src={design.image}
                                                    alt={design.title}
                                                    key={design.id}
                                                    width={340}
                                                    height={340}
                                                    variations={['lazyload']}
                                                />
                                            );
                                        })}
                                        variations={['centered-indicators', 'without-shadow']}
                                        showTriggersOnHover={true}
                                        disableSwipeToNavigate={true}
                                    />
                                }
                                cta={
                                    <Button
                                        href={invitePath(designer.id)}
                                        variations={['primary', 'fluid', 'chameleon']}
                                    >
                                        {__('Invite to work')}
                                    </Button>
                                }
                            />
                        </div>
                    );
                })}
            </Scrollable>
            <div className="centered-text">
                <CTALink href={designerSearchPath(contestCategory.key)} color="heritage">
                    {formatMessage(__('Discover more {categoryTitle} designers'), {
                        categoryTitle: contestCategory.title,
                    })}
                </CTALink>
            </div>
        </>
    );
}

export default Designers;
